// client/src/pages/Resources.tsx

import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
    memo,
} from 'react';
import {
    ArrowLeft,
    ArrowRight,
    BookMarked,
    BookOpen,
    Download,
    Film,
    FileText,
    Library,
    Lightbulb,
    Newspaper,
    Wrench,
    type LucideIcon,
} from 'lucide-react';
import './Resources.css';

import {
    Resource,
    ResourceCategory,
    ResourceCategoryLabels,
    ResourceParentCategoryMapping,
    ResourceParentCategoryLabels,
} from '../shared/models/Resource';

const ITEMS_PER_PAGE = 12;

// ============================================
// CLOUDINARY IMAGE OPTIMIZATION
// ============================================

const getOptimizedImageUrl = (
    url: string,
    options?: {
        width?: number;
        height?: number;
        quality?: string;
    }
) => {
    if (!url || !url.includes('cloudinary')) {
        return url;
    }

    const {
        width = 400,
        height = 250,
        quality = 'auto',
    } = options || {};

    return url.replace(
        '/upload/',
        `/upload/w_${width},h_${height},c_fill,q_${quality},f_auto/`
    );
};

// ============================================
// CLOUDINARY DOWNLOAD DELIVERY
// ============================================

/**
 * Converts a Cloudinary upload URL into an attachment
 * delivery URL so supported files are downloaded rather
 * than opened directly in the browser.
 *
 * The original URL stored in MongoDB is not changed.
 */
const getCloudinaryDownloadUrl = (url: string): string => {
    if (!url || !url.includes('res.cloudinary.com')) {
        return url;
    }

    if (url.includes('/image/upload/fl_attachment/')) {
        return url;
    }

    if (url.includes('/image/upload/')) {
        return url.replace(
            '/image/upload/',
            '/image/upload/fl_attachment/'
        );
    }

    return url;
};

// ============================================
// RESOURCE LIBRARY ICONS
// ============================================

/**
 * Icons for the top-level Resource Library categories.
 *
 * The keys correspond to ResourceParentCategoryMapping.
 */
const parentCategoryIcons: Record<string, LucideIcon> = {
    publications: BookOpen,
    data: Lightbulb,
    reference: BookMarked,
    media: Film,
    tools: Wrench,
    press: Newspaper,
};

/**
 * Get the appropriate Lucide icon for a Resource Library
 * parent category.
 */
const getParentCategoryIcon = (
    category: string
): LucideIcon => {
    return parentCategoryIcons[category] || Library;
};

// ============================================
// MEMOIZED RESOURCE CARD
// ============================================

const ResourceCard = memo(
    ({ resource }: { resource: Resource }) => {
        const formatFileSize = (bytes?: number) => {
            if (!bytes) {
                return '';
            }

            if (bytes < 1024) {
                return `${bytes} B`;
            }

            if (bytes < 1024 * 1024) {
                return `${(bytes / 1024).toFixed(1)} KB`;
            }

            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        };

        const downloadUrl = resource.mainFile
            ? getCloudinaryDownloadUrl(resource.mainFile.url)
            : '';

        return (
            <div className="resource-card">
                {/* Resource Thumbnail */}
                {resource.media &&
                    resource.media.length > 0 && (
                        <div className="resource-thumbnail">
                            <img
                                src={getOptimizedImageUrl(
                                    resource.media[0].thumbnail ||
                                        resource.media[0].url,
                                    {
                                        width: 400,
                                        height: 250,
                                        quality: 'auto',
                                    }
                                )}
                                alt={resource.title}
                                loading="lazy"
                            />
                        </div>
                    )}

                <div className="resource-info">
                    <h4>{resource.title}</h4>

                    <p>{resource.description}</p>

                    {/* Resource Metadata */}
                    <div className="resource-meta">
                        <span className="resource-date">
                            {new Date(
                                resource.publishedDate
                            ).toLocaleDateString()}
                        </span>

                        {resource.authors &&
                            resource.authors.length > 0 && (
                                <span className="resource-author">
                                    {resource.authors[0].name}
                                </span>
                            )}

                        {resource.mainFile && (
                            <span className="resource-size">
                                {formatFileSize(
                                    resource.mainFile.size
                                )}
                            </span>
                        )}
                    </div>

                    {/* Resource Actions */}
                    <div className="resource-actions">
                        {resource.mainFile && (
                            <a
                                href={downloadUrl}
                                className="btn-download"
                                aria-label={`Download ${resource.mainFile.filename}`}
                            >
                                <Download
                                    size={17}
                                    aria-hidden="true"
                                />
                                <span>Download</span>
                            </a>
                        )}

                        <a
                            href={`/resources/${
                                resource.slug || resource._id
                            }`}
                            className="btn-read"
                            aria-label={`Read more about ${resource.title}`}
                        >
                            <span>Read More</span>
                            <ArrowRight
                                size={17}
                                aria-hidden="true"
                            />
                        </a>
                    </div>
                </div>
            </div>
        );
    }
);

ResourceCard.displayName = 'ResourceCard';

// ============================================
// SKELETON LOADER
// ============================================

const SkeletonCard = memo(() => (
    <div
        className="resource-card skeleton-card"
        aria-hidden="true"
    >
        <div className="skeleton-thumbnail"></div>

        <div className="resource-info">
            <div className="skeleton-line skeleton-title"></div>
            <div className="skeleton-line"></div>
            <div className="skeleton-line skeleton-short"></div>

            <div className="skeleton-meta">
                <div className="skeleton-line skeleton-meta-item"></div>
                <div className="skeleton-line skeleton-meta-item"></div>
            </div>

            <div className="skeleton-actions">
                <div className="skeleton-button"></div>
                <div className="skeleton-button"></div>
            </div>
        </div>
    </div>
));

SkeletonCard.displayName = 'SkeletonCard';

// ============================================
// MAIN RESOURCES COMPONENT
// ============================================

const Resources: React.FC = () => {
    const [resources, setResources] = useState<Resource[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeParentCategory, setActiveParentCategory] =
        useState('all');
    const [currentPage, setCurrentPage] = useState(1);

    // ============================================
    // INITIALIZE FROM CACHE
    // ============================================

    useEffect(() => {
        const cachedResources =
            sessionStorage.getItem('resources_cache');

        const cacheTimestamp =
            sessionStorage.getItem('resources_cache_time');

        const now = Date.now();

        // Use cache if it is less than 10 minutes old.
        if (
            cachedResources &&
            cacheTimestamp &&
            now - parseInt(cacheTimestamp, 10) <
                10 * 60 * 1000
        ) {
            try {
                const parsed = JSON.parse(cachedResources);

                setResources(parsed);
                setLoading(false);

                return;
            } catch {
                // If cache is corrupted, fetch fresh data.
                console.warn(
                    'Cache parse error, fetching fresh data'
                );
            }
        }

        fetchResources();
    }, []);

    // ============================================
    // RESET PAGINATION WHEN FILTER CHANGES
    // ============================================

    useEffect(() => {
        setCurrentPage(1);
    }, [activeParentCategory]);

    // ============================================
    // FETCH RESOURCES
    // ============================================

    const fetchResources = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await fetch(
                '/.netlify/functions/public-resources'
            );

            if (!response.ok) {
                throw new Error('Failed to fetch resources');
            }

            const data = await response.json();

            const publishedResources = (
                data.data || []
            ).filter(
                (resource: Resource) =>
                    resource.status === 'published'
            );

            // Cache published resources.
            sessionStorage.setItem(
                'resources_cache',
                JSON.stringify(publishedResources)
            );

            sessionStorage.setItem(
                'resources_cache_time',
                Date.now().toString()
            );

            setResources(publishedResources);
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to load resources'
            );

            console.error(
                'Error fetching resources:',
                err
            );
        } finally {
            setLoading(false);
        }
    }, []);

    // ============================================
    // PARENT CATEGORIES
    // ============================================

    const parentCategories = useMemo(
        () => [
            'all',
            ...Object.keys(ResourceParentCategoryMapping),
        ],
        []
    );

    // ============================================
    // FILTER RESOURCES
    // ============================================

    const getFilteredResources = useCallback(() => {
        if (activeParentCategory === 'all') {
            return resources;
        }

        const categories =
            ResourceParentCategoryMapping[
                activeParentCategory
            ] || [];

        return resources.filter((resource) =>
            categories.includes(resource.category)
        );
    }, [activeParentCategory, resources]);

    // ============================================
    // GROUP RESOURCES BY SUBCATEGORY
    // ============================================

    const getGroupedResources = useCallback(
        (resourcesList: Resource[]) => {
            const grouped: Record<string, Resource[]> = {};

            resourcesList.forEach((resource) => {
                const label =
                    ResourceCategoryLabels[
                        resource.category
                    ];

                if (!grouped[label]) {
                    grouped[label] = [];
                }

                grouped[label].push(resource);
            });

            return grouped;
        },
        []
    );

    // ============================================
    // CATEGORY COUNT
    // ============================================

    const getCategoryCount = useCallback(
        (category: string) => {
            if (category === 'all') {
                return resources.length;
            }

            const categories =
                ResourceParentCategoryMapping[category] ||
                [];

            return resources.filter((resource) =>
                categories.includes(resource.category)
            ).length;
        },
        [resources]
    );

    // ============================================
    // DERIVED RESOURCE DATA
    // ============================================

    const filteredResources = useMemo(
        () => getFilteredResources(),
        [getFilteredResources]
    );

    const totalPages = useMemo(
        () =>
            Math.max(
                1,
                Math.ceil(
                    filteredResources.length /
                        ITEMS_PER_PAGE
                )
            ),
        [filteredResources.length]
    );

    const currentPageResources = useMemo(() => {
        const startIndex =
            (currentPage - 1) * ITEMS_PER_PAGE;

        return filteredResources.slice(
            startIndex,
            startIndex + ITEMS_PER_PAGE
        );
    }, [currentPage, filteredResources]);

    const groupedResources = useMemo(
        () =>
            getGroupedResources(
                currentPageResources
            ),
        [
            currentPageResources,
            getGroupedResources,
        ]
    );

    // ============================================
    // LOADING STATE
    // ============================================

    if (loading) {
        return (
            <div className="resources-container">
                <div className="resources-hero">
                    <h1>Resources</h1>

                    <p className="resources-intro">
                        Access our publications, data,
                        reference materials, and more
                    </p>
                </div>

                <div className="loading-state">
                    <div className="loading-spinner">
                        <div className="spinner-animation"></div>

                        <p>
                            Loading resources...
                        </p>
                    </div>

                    <div
                        className="skeleton-grid"
                        aria-hidden="true"
                    >
                        {Array.from({
                            length: 6,
                        }).map((_, index) => (
                            <SkeletonCard
                                key={index}
                            />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // ============================================
    // ERROR STATE
    // ============================================

    if (error && resources.length === 0) {
        return (
            <div className="resources-container">
                <div className="resources-hero">
                    <h1>Resources</h1>

                    <p className="resources-intro">
                        Access our publications, data,
                        reference materials, and more
                    </p>
                </div>

                <div className="error-state">
                    <h2>Unable to Load Resources</h2>

                    <p>{error}</p>

                    <button
                        type="button"
                        onClick={fetchResources}
                        className="btn-retry"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    // ============================================
    // SUCCESS STATE
    // ============================================

    return (
        <div className="resources-container">
            {/* Hero Section */}
            <div className="resources-hero">
                <h1>Resources</h1>

                <p className="resources-intro">
                    Access our publications, data,
                    reference materials, and more
                </p>
            </div>

            {/* Quick Stats */}
            <div className="resource-stats">
                <div className="stat-item">
                    <span className="stat-number">
                        {resources.length}
                    </span>

                    <span className="stat-label">
                        Total Resources
                    </span>
                </div>

                <div className="stat-item">
                    <span className="stat-number">
                        {
                            resources.filter(
                                (resource) =>
                                    resource.isFeatured
                            ).length
                        }
                    </span>

                    <span className="stat-label">
                        Featured
                    </span>
                </div>

                <div className="stat-item">
                    <span className="stat-number">
                        {resources.reduce(
                            (total, resource) =>
                                total +
                                (resource.downloadCount ||
                                    0),
                            0
                        )}
                    </span>

                    <span className="stat-label">
                        Total Downloads
                    </span>
                </div>
            </div>

            {/* ========================================
                PARENT CATEGORY NAVIGATION
            ======================================== */}

            <nav
                className="resource-nav"
                aria-label="Resource categories"
            >
                {parentCategories.map((category) => {
                    const Icon =
                        category === 'all'
                            ? Library
                            : getParentCategoryIcon(
                                  category
                              );

                    const label =
                        category === 'all'
                            ? 'All Resources'
                            : ResourceParentCategoryLabels[
                                  category
                              ];

                    return (
                        <button
                            key={category}
                            type="button"
                            className={
                                activeParentCategory ===
                                category
                                    ? 'active'
                                    : ''
                            }
                            onClick={() =>
                                setActiveParentCategory(
                                    category
                                )
                            }
                            aria-pressed={
                                activeParentCategory ===
                                category
                            }
                        >
                            <Icon
                                size={20}
                                strokeWidth={2}
                                aria-hidden="true"
                            />

                            <span>{label}</span>

                            <span className="category-count">
                                {getCategoryCount(
                                    category
                                )}
                            </span>
                        </button>
                    );
                })}
            </nav>

            {/* ========================================
                PARENT CATEGORY HEADER
            ======================================== */}

            {activeParentCategory !== 'all' && (
                <div className="parent-category-header">
                    <h2>
                        {
                            ResourceParentCategoryLabels[
                                activeParentCategory
                            ]
                        }
                    </h2>

                    <p>
                        Explore our{' '}
                        {activeParentCategory}{' '}
                        resources
                    </p>
                </div>
            )}

            {/* ========================================
                RESOURCES DISPLAY
            ======================================== */}

            <div className="resources-grid">
                {Object.keys(groupedResources).length >
                0 ? (
                    <>
                        {/* Pagination Summary */}
                        <div className="pagination-summary">
                            <p>
                                Showing{' '}
                                {filteredResources.length >
                                0
                                    ? Math.min(
                                          (currentPage -
                                              1) *
                                              ITEMS_PER_PAGE +
                                              1,
                                          filteredResources.length
                                      )
                                    : 0}
                                -
                                {Math.min(
                                    currentPage *
                                        ITEMS_PER_PAGE,
                                    filteredResources.length
                                )}{' '}
                                of{' '}
                                {
                                    filteredResources.length
                                }{' '}
                                resources
                            </p>
                        </div>

                        {/* Resource Groups */}
                        {Object.entries(
                            groupedResources
                        ).map(
                            ([
                                subcategory,
                                items,
                            ]) => (
                                <div
                                    key={
                                        subcategory
                                    }
                                    className="resource-group"
                                >
                                    <h3 className="subcategory-title">
                                        {
                                            subcategory
                                        }
                                    </h3>

                                    <div className="resource-cards">
                                        {items.map(
                                            (
                                                resource
                                            ) => (
                                                <ResourceCard
                                                    key={
                                                        resource._id
                                                    }
                                                    resource={
                                                        resource
                                                    }
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            )
                        )}

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="pagination-controls">
                                <button
                                    type="button"
                                    className="pagination-btn"
                                    onClick={() =>
                                        setCurrentPage(
                                            (previous) =>
                                                Math.max(
                                                    1,
                                                    previous -
                                                        1
                                                )
                                        )
                                    }
                                    disabled={
                                        currentPage ===
                                        1
                                    }
                                >
                                    <ArrowLeft
                                        size={17}
                                        aria-hidden="true"
                                    />

                                    <span>
                                        Previous
                                    </span>
                                </button>

                                <span className="pagination-status">
                                    Page{' '}
                                    {currentPage} of{' '}
                                    {totalPages}
                                </span>

                                <button
                                    type="button"
                                    className="pagination-btn"
                                    onClick={() =>
                                        setCurrentPage(
                                            (previous) =>
                                                Math.min(
                                                    totalPages,
                                                    previous +
                                                        1
                                                )
                                        )
                                    }
                                    disabled={
                                        currentPage ===
                                        totalPages
                                    }
                                >
                                    <span>Next</span>

                                    <ArrowRight
                                        size={17}
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="no-resources">
                        <FileText
                            size={42}
                            strokeWidth={1.5}
                            aria-hidden="true"
                        />

                        <p>
                            No resources available in
                            this category yet.
                        </p>

                        <p className="no-resources-sub">
                            Check back soon for new
                            content.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default memo(Resources);